// src/pages/Problems/CreateProblemPage.js

import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, TextField, Button, Box, Grid,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
  CircularProgress, Alert, Stack, IconButton, Divider, InputAdornment,
  GlobalStyles
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  ArrowBack, Save, Business, Payments, Info
} from '@mui/icons-material';
import api from '../../services/api';
import { useAppSelector } from '../../store/hooks';

// Rengigs Official Branding
const rengigsPalette = {
  forestGreen: '#2E7D32',
  deepForest: '#1B5E20',
  mediumGreen: '#4CAF50',
  lightGreen: '#8BC34A',
  brandLime: '#CDDC39',
  charcoal: '#1A2A3A',
  softGreen: 'rgba(46, 125, 50, 0.06)',
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
  gradientReverse: 'linear-gradient(135deg, #CDDC39 0%, #8BC34A 40%, #4CAF50 70%, #2E7D32 100%)',
};

const scrollbarStyles = {
  '*::-webkit-scrollbar': { width: '10px', height: '10px' },
  '*::-webkit-scrollbar-track': { background: rengigsPalette.offWhite, borderRadius: '10px' },
  '*::-webkit-scrollbar-thumb': {
    background: rengigsPalette.gradient, borderRadius: '10px', transition: 'all 0.3s ease',
  },
  '*::-webkit-scrollbar-thumb:hover': { background: rengigsPalette.gradientReverse },
  '*': { scrollbarWidth: 'thin', scrollbarColor: `${rengigsPalette.forestGreen} ${rengigsPalette.offWhite}` },
};

const validationSchema = yup.object({
  title: yup.string().required('Title is required').max(255),
  description: yup.string().required('Description is required').max(5000),
  background: yup.string().max(5000),
  priority: yup.string().required('Priority is required'),
  visibility: yup.string().required('Visibility is required'),
  desired_timeline: yup.string(),
  expected_budget: yup.number().min(0).nullable().transform((v, o) => o === '' ? null : v),
  expected_budget_currency: yup.string().default('USD'),
  deadline_at: yup.string().nullable(),
  organization_id: yup.string().required('Organization is required'),
});

// ✅ CRITICAL: Section wrapper defined OUTSIDE the component.
// Defining it inside causes React to remount it on every keystroke,
// killing input focus and preventing typing.
const SectionCard = ({ icon: Icon, title, bgcolor, children }) => (
  <Paper sx={{
    p: 4, borderRadius: '24px',
    border: `1px solid ${rengigsPalette.border}`,
    bgcolor: bgcolor || rengigsPalette.white,
  }}>
    {title && (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
        {Icon && <Icon sx={{ color: rengigsPalette.forestGreen }} />}
        <Typography variant="h6" sx={{ fontWeight: 800, color: rengigsPalette.charcoal }}>
          {title}
        </Typography>
      </Stack>
    )}
    {children}
  </Paper>
);

const CreateProblemPage = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const data = await api.get('/organizations/my-organizations/');
      const orgs = Array.isArray(data) ? data : (data?.results || []);
      setOrganizations(orgs);
      // Auto-select if only one org
      if (orgs.length === 1) {
        formik.setFieldValue('organization_id', orgs[0].id);
      }
    } catch (err) {
      setError('Failed to load organizations. Please try again.');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      background: '',
      priority: 'medium',
      visibility: 'public',
      desired_timeline: '',
      expected_budget: '',
      expected_budget_currency: 'USD',
      deadline_at: '',
      organization_id: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        setError(null);

        const payload = {
          title: values.title,
          description: values.description,
          background: values.background || '',
          priority: values.priority,
          visibility: values.visibility,
          desired_timeline: values.desired_timeline || '',
          expected_budget: values.expected_budget !== '' ? parseFloat(values.expected_budget) : null,
          expected_budget_currency: values.expected_budget_currency,
          deadline_at: values.deadline_at || null,
          organization_id: values.organization_id,
        };

        await api.post('/problems/', payload);
        // Navigate back — the list will reload and show the new problem immediately
        navigate('/problems');
      } catch (err) {
        if (err.data && typeof err.data === 'object') {
          const msgs = Object.entries(err.data)
            .map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(', ') : m}`)
            .join('\n');
          setError(msgs || 'Check your input and try again.');
        } else {
          setError(err.message || 'Failed to create problem.');
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (loadingOrgs) {
    return (
      <>
        <GlobalStyles styles={scrollbarStyles} />
        <Container sx={{ display: 'flex', justifyContent: 'center', py: 15 }}>
          <CircularProgress sx={{ color: rengigsPalette.forestGreen }} />
        </Container>
      </>
    );
  }

  if (organizations.length === 0) {
    return (
      <>
        <GlobalStyles styles={scrollbarStyles} />
        <Container maxWidth="sm" sx={{ py: 10 }}>
          <Paper sx={{
            p: 6, textAlign: 'center', borderRadius: '24px',
            border: `1px solid ${rengigsPalette.border}`,
          }}>
            <Business sx={{ fontSize: 60, color: rengigsPalette.forestGreen, mb: 2, opacity: 0.3 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: rengigsPalette.charcoal }}>
              No Organization Found
            </Typography>
            <Typography color="text.secondary" paragraph>
              You need an active organization to post industry challenges.
            </Typography>
            <Button
              variant="contained" size="large"
              onClick={() => navigate('/organizations/create')}
              sx={{
                background: rengigsPalette.gradient, color: 'white',
                px: 4, py: 1.5, borderRadius: '12px', fontWeight: 700, textTransform: 'none',
                '&:hover': { background: rengigsPalette.forestGreen, transform: 'translateY(-2px)' },
              }}
            >
              Create Organization
            </Button>
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <GlobalStyles styles={scrollbarStyles} />
      <Container maxWidth="md" sx={{ py: 6 }}>

        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
          <IconButton
            onClick={() => navigate('/problems')}
            sx={{ border: `1px solid ${rengigsPalette.border}` }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: rengigsPalette.charcoal }}>
              Post a Challenge
            </Typography>
            <Typography variant="body2" sx={{ color: '#5F6D7C' }}>
              Describe your problem to receive global solutions.
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 4, borderRadius: '12px' }} onClose={() => setError(null)}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{error}</pre>
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={4}>

            {/* ── Section 1: Core Details ── */}
            <SectionCard icon={Info} title="Core Details">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth name="title" label="What is the problem title?"
                    placeholder="e.g., Warehouse Pallet Inspection AI"
                    value={formik.values.title} onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                    disabled={submitting}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth multiline rows={4} name="description" label="Detailed Description"
                    placeholder="Explain the technical or operational bottleneck..."
                    value={formik.values.description} onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    disabled={submitting}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth multiline rows={2} name="background"
                    label="Context / Background (Optional)"
                    placeholder="Previous attempts to solve or current equipment used..."
                    value={formik.values.background} onChange={formik.handleChange}
                    disabled={submitting}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── Section 2: Classification ── */}
            <SectionCard bgcolor={rengigsPalette.softGreen}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl
                    fullWidth
                    error={formik.touched.organization_id && Boolean(formik.errors.organization_id)}
                  >
                    <InputLabel>Organization *</InputLabel>
                    <Select
                      name="organization_id" label="Organization *"
                      value={formik.values.organization_id}
                      onChange={formik.handleChange} disabled={submitting}
                      sx={{ borderRadius: '12px', bgcolor: rengigsPalette.white }}
                    >
                      {organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.organization_id && formik.errors.organization_id && (
                      <FormHelperText>{formik.errors.organization_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      name="priority" label="Priority"
                      value={formik.values.priority}
                      onChange={formik.handleChange} disabled={submitting}
                      sx={{ borderRadius: '12px', bgcolor: rengigsPalette.white }}
                    >
                      <MenuItem value="critical">Critical</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Visibility</InputLabel>
                    <Select
                      name="visibility" label="Visibility"
                      value={formik.values.visibility}
                      onChange={formik.handleChange} disabled={submitting}
                      sx={{ borderRadius: '12px', bgcolor: rengigsPalette.white }}
                    >
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="registered">Registered Only</MenuItem>
                      <MenuItem value="verified">Verified Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── Section 3: Timeline & Budget ── */}
            <SectionCard icon={Payments} title="Timeline & Budget">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth name="desired_timeline" label="Desired Timeline"
                    placeholder="e.g., 6 months"
                    value={formik.values.desired_timeline} onChange={formik.handleChange}
                    disabled={submitting}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth type="date" name="deadline_at" label="Submission Deadline"
                    value={formik.values.deadline_at} onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }} disabled={submitting}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth type="number" name="expected_budget"
                      label="Expected Budget (Optional)"
                      value={formik.values.expected_budget} onChange={formik.handleChange}
                      disabled={submitting}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                    <FormControl sx={{ minWidth: 110 }}>
                      <Select
                        name="expected_budget_currency"
                        value={formik.values.expected_budget_currency}
                        onChange={formik.handleChange} disabled={submitting}
                        sx={{ borderRadius: '12px' }}
                      >
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                        <MenuItem value="NGN">NGN</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── Actions ── */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
              <Button
                size="large" onClick={() => navigate('/problems')}
                disabled={submitting}
                sx={{ color: rengigsPalette.forestGreen, fontWeight: 700, textTransform: 'none' }}
              >
                Discard
              </Button>
              <Button
                type="submit" variant="contained" size="large"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
                disabled={submitting}
                sx={{
                  background: rengigsPalette.gradient, backgroundSize: '200% auto',
                  px: 6, py: 1.5, borderRadius: '12px', fontWeight: 800,
                  textTransform: 'none',
                  boxShadow: `0 8px 20px -6px ${rengigsPalette.forestGreen}60`,
                  transition: '0.3s',
                  '&:hover': {
                    background: rengigsPalette.forestGreen,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 24px -5px ${rengigsPalette.forestGreen}80`,
                  },
                  '&:disabled': { background: '#E0E0E0', color: '#9E9E9E' },
                }}
              >
                {submitting ? 'Posting...' : 'Post Challenge'}
              </Button>
            </Box>

          </Stack>
        </form>
      </Container>
    </>
  );
};

export default CreateProblemPage;